import { useState, type FormEvent } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Check, KeyRound, Loader2, MoreHorizontal, Trash2, UserPlus, X } from "lucide-react";
import {
  SECTION_CONFIGS,
  type AdminSection,
  type AdminUser,
} from "../../../../shared/index";
import { useAuth } from "@/contexts/auth-context";
import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const USERS_QUERY_KEY = ["admin", "users"] as const;

const visibleSections = SECTION_CONFIGS.filter((s) => !s.hidden);

function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const [tempPassword, setTempPassword] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const usersQuery = useQuery({
    queryKey: USERS_QUERY_KEY,
    queryFn: api.adminUsers.list,
  });

  if (!user) return null;

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Admin settings</h1>
          <p className="text-sm text-muted-foreground">
            Create admin users, manage section access, and reset passwords.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <UserPlus className="h-4 w-4" />
          Add user
        </Button>
      </header>

      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">How admin access works</p>
        <ul className="mt-2 space-y-1">
          <li>• Admins create users with a generated temporary password.</li>
          <li>• New users are forced to change their password on first login.</li>
          <li>• Permission and password changes take effect on the user's next sign-in.</li>
        </ul>
      </div>

      {tempPassword && (
        <TempPasswordBanner
          info={tempPassword}
          onDismiss={() => setTempPassword(null)}
        />
      )}

      {usersQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : usersQuery.isError ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load users: {describeError(usersQuery.error)}
        </div>
      ) : (
        <UsersTable
          users={usersQuery.data?.users ?? []}
          currentUserId={user.id}
          onTempPassword={setTempPassword}
        />
      )}

      <CreateUserDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(info) => {
          setTempPassword(info);
          setAddOpen(false);
        }}
      />
    </div>
  );
}

function UsersTable({
  users,
  currentUserId,
  onTempPassword,
}: {
  users: AdminUser[];
  currentUserId: string;
  onTempPassword: (info: { email: string; password: string }) => void;
}) {
  if (users.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
        No admin users yet. Click "Add user" to create one.
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="px-4">User</TableHead>
            <TableHead className="px-3 text-center">Status</TableHead>
            {visibleSections.map((section) => (
              <TableHead
                key={section.key}
                className="px-2 text-center text-xs"
                title={section.description}
              >
                {section.label}
              </TableHead>
            ))}
            <TableHead className="w-16 px-3" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              isSelf={u.id === currentUserId}
              onTempPassword={onTempPassword}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function UserRow({
  user,
  isSelf,
  onTempPassword,
}: {
  user: AdminUser;
  isSelf: boolean;
  onTempPassword: (info: { email: string; password: string }) => void;
}) {
  const queryClient = useQueryClient();

  const togglePermission = useMutation({
    mutationFn: ({
      section,
      enabled,
    }: {
      section: AdminSection;
      enabled: boolean;
    }) => api.adminUsers.setPermission(user.id, section, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const toggleStatus = useMutation({
    mutationFn: (isActive: boolean) => api.adminUsers.setStatus(user.id, isActive),
    onSuccess: (_, isActive) => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
      toast.success(isActive ? "User activated" : "User deactivated");
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const resetPassword = useMutation({
    mutationFn: () => api.adminUsers.resetPassword(user.id),
    onSuccess: ({ tempPassword }) => {
      onTempPassword({ email: user.email, password: tempPassword });
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const deleteUser = useMutation({
    mutationFn: () => api.adminUsers.delete(user.id),
    onSuccess: () => {
      toast.success(`Deleted ${user.email}`);
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  return (
    <TableRow>
      <TableCell className="px-4">
        <div>
          <p className="font-medium">{user.name}</p>
          <p className="text-xs text-muted-foreground">{user.email}</p>
          {user.mustChangePassword && (
            <Badge variant="warning" className="mt-1">
              Pending password reset
            </Badge>
          )}
        </div>
      </TableCell>

      <TableCell className="px-3 text-center">
        <button
          onClick={() => toggleStatus.mutate(!user.isActive)}
          disabled={toggleStatus.isPending}
          className="inline-flex items-center gap-1"
        >
          {toggleStatus.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Badge variant={user.isActive ? "success" : "destructive"}>
              {user.isActive ? "Active" : "Inactive"}
            </Badge>
          )}
        </button>
      </TableCell>

      {visibleSections.map((section) => {
        const enabled = user.permissions[section.key as AdminSection];
        const key = `${user.id}-${section.key}`;
        const pending =
          togglePermission.isPending &&
          togglePermission.variables?.section === section.key;
        return (
          <TableCell key={key} className="px-2 text-center">
            <button
              onClick={() =>
                togglePermission.mutate({
                  section: section.key as AdminSection,
                  enabled: !enabled,
                })
              }
              disabled={pending}
              className="inline-flex h-7 w-7 items-center justify-center rounded transition-colors"
              title={`${enabled ? "Revoke" : "Grant"} ${section.label}`}
            >
              {pending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : enabled ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </TableCell>
        );
      })}

      <TableCell className="px-3 text-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => resetPassword.mutate()}
              disabled={resetPassword.isPending}
            >
              <KeyRound className="h-4 w-4" />
              Reset password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                if (isSelf) {
                  toast.error("You cannot delete your own account");
                  return;
                }
                if (confirm(`Delete ${user.email}? This cannot be undone.`)) {
                  deleteUser.mutate();
                }
              }}
              disabled={deleteUser.isPending || isSelf}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Delete user
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

function CreateUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (info: { email: string; password: string }) => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const createUser = useMutation({
    mutationFn: () => api.adminUsers.create({ email, name }),
    onSuccess: ({ user, tempPassword }) => {
      onCreated({ email: user.email, password: tempPassword });
      setEmail("");
      setName("");
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    createUser.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add admin user</DialogTitle>
          <DialogDescription>
            A temporary password will be generated. Share it securely with the
            new user — they will be forced to change it on first login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-user-email">Email</Label>
            <Input
              id="new-user-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@tipranks.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-user-name">Name</Label>
            <Input
              id="new-user-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Create user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TempPasswordBanner({
  info,
  onDismiss,
}: {
  info: { email: string; password: string };
  onDismiss: () => void;
}) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-700 dark:bg-amber-900/20">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium text-amber-900 dark:text-amber-200">
            Temporary password for {info.email}
          </p>
          <p className="mt-1 text-amber-800 dark:text-amber-300">
            Share this with the user securely — it will not be shown again.
          </p>
          <code className="mt-2 inline-block rounded bg-amber-100 px-2 py-1 font-mono text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
            {info.password}
          </code>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
